import { Buy } from './Buy'
import { Sell } from './Sell'
import { Form } from '@/ui/atoms/form/Form'
import { ToggleButton } from './ToggleButton'
import { Button } from '@/ui/atoms/button/Button'
import { UseSwapResults } from '@/features/swap/logic/types'
import { capitalize } from '@/domain/common/format'
import { Switch } from '@/ui/atoms/switch/Switch'
import { Typography } from '@/ui/atoms/typography/Typography'
import { Tooltip } from '@/ui/atoms/tooltip/Tooltip'
import { TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { TooltipContentShort } from '@/ui/atoms/tooltip/Tooltip'
import { InfoIcon } from 'lucide-react'
import { Panel } from '@/ui/atoms/panel/Panel'
import { TokenSymbol } from '@/domain/types/TokenSymbol'

export function SwapForm(props: UseSwapResults & { lockedToToken?: boolean; featuredToAssets?: TokenSymbol[] }) {
  const {
    form,
    guestMode,
    fromTokenField,
    toTokenField,
    swapMode,
    swapState,
    quoteError,
    surgeAcknowledged,
    setSurgeAcknowledged,
    priceData,
    pageStatus,
    wrapUnwrap,
    swap,
    openConnectModal,
    swapTokens,
    lockedToToken,
    featuredToAssets,
  } = props

  const surgeProtectionApplied = priceData && 'result' in priceData && priceData.result.surgeProtectionApplied

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(() => {})}>
        <div className="relative z-10">
          <Sell
            control={form.control}
            assets={fromTokenField.availableAssets}
            selectedAsset={fromTokenField.selectedAsset}
            changeToken={fromTokenField.changeToken}
            maxSelectedFieldName={fromTokenField.maxSelectedFieldName}
            onChange={fromTokenField.onChange}
            onSetMax={fromTokenField.onSetMax}
            maxValue={fromTokenField.maxValue}
          />
          {!lockedToToken && (
            <ToggleButton
              className="absolute right-1/2 bottom-0 translate-x-1/2 translate-y-3/4"
              onClick={swapTokens}
            />
          )}
        </div>
        <Buy
          control={form.control}
          assets={toTokenField.availableAssets}
          selectedAsset={toTokenField.selectedAsset}
          changeToken={toTokenField.changeToken}
          onChange={toTokenField.onChange}
          maxSelectedFieldName={toTokenField.maxSelectedFieldName}
          featuredAssets={featuredToAssets}
        />

        {surgeProtectionApplied && (
          <Panel className="mt-2 gap-2 border-gradient-yellow/10 bg-gradient-yellow/5 p-4 md:p-4">
            <Panel.Header className="mb-1 flex items-center gap-2">
              <Typography className="font-semibold text-white">Surge protection</Typography>
              <Tooltip>
                <TooltipTrigger>
                  <InfoIcon className="h-4 w-4" />
                </TooltipTrigger>
                <TooltipContentShort className="text-white/80">
                  GlueX prevents value loss from poor liquidity with extra controls. Its{' '}
                  <a
                    href="https://docs.gluex.xyz/gluex-apis/router-api/#surge-protection"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-bg underline"
                  >
                    Surge Protection Mechanism
                  </a>{' '}
                  simulates trade reversals to assess liquidity risk, protecting you from value loss that exceeds
                  acceptable limits.
                </TooltipContentShort>
              </Tooltip>
            </Panel.Header>
            <Panel.Content className=" flex items-center gap-8 ">
              <Typography className="text-sm text-white/80">
                {priceData?.result?.surgeValue ? (
                  <span>
                    Unrealized price impact{' '}
                    <span className="font-semibold text-white">{Math.abs(priceData.result.surgeValue)}%</span>.{' '}
                  </span>
                ) : (
                  ''
                )}
                Surge protection applied to save your funds from high value loss.
              </Typography>
              <Switch className="" onClick={() => setSurgeAcknowledged(!surgeAcknowledged)} checked={surgeAcknowledged}>
                I acknowledge
              </Switch>
            </Panel.Content>
          </Panel>
        )}

        {(quoteError || (priceData && 'error' in priceData)) && (
          <Panel className="mt-2 gap-2 p-4 md:p-4">
            <Panel.Header className="font-semibold text-white">No routes available</Panel.Header>
            <Panel.Content>
              <Typography className="text-sm text-white/50">
                Reasons for that could be: low liquidity, amount selected is too low, gas costs are too high or there
                are no routes for the selected combination.
              </Typography>
            </Panel.Content>
          </Panel>
        )}

        <div className="mt-4">
          {guestMode ? (
            <Button className="w-full rounded-lg" onClick={openConnectModal}>
              Connect wallet
            </Button>
          ) : (
            <div className="relative">
              <Button
                className={'w-full rounded-lg'}
                onClick={() => {
                  if (swapMode === 'swap') {
                    swap()
                    return
                  }
                  wrapUnwrap()
                }}
                disabled={!pageStatus.actionsEnabled || !!swapState || (!!surgeProtectionApplied && !surgeAcknowledged)}
              >
                {swapState ? swapState : capitalize(swapMode)}
              </Button>
            </div>
          )}
        </div>
      </form>
    </Form>
  )
}
